import * as React from 'react';
import { Popper } from '@mui/base/Popper';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { useImperativeHandle } from 'react';
import Typography from '../Typography/Typography';

type Props = React.PropsWithChildren<{
  content?: React.ReactElement;
  actionsRef?: any;
  disabled?: boolean;
}> &
  Pick<React.ComponentProps<typeof Popper>, 'placement'>;

type PopoverContextState = {
  element: HTMLElement | null;
  content: React.ReactNode;
};

type PopOverContextActions = {
  open(args: PopoverContextState): void;
  close(): void;
};

const defaultState: PopoverContextState = {
  element: null,
  content: null,
};

const PopoverContext = React.createContext<
  PopoverContextState & PopOverContextActions
>({
  ...defaultState,
  open(args: PopoverContextState) {},
  close() {},
});

function isConnectedAnchor(
  element: HTMLElement | null,
): element is HTMLElement {
  return element?.isConnected === true;
}

export const PopoverProvider = (props: React.PropsWithChildren) => {
  const [value, setValue] = React.useState<PopoverContextState>(defaultState);

  const close = React.useCallback(() => {
    setValue(defaultState);
  }, []);

  const open = React.useCallback((args: PopoverContextState) => {
    setValue(args);
  }, []);

  React.useEffect(() => {
    const element = value.element;
    if (!element) {
      return;
    }

    let frameId = 0;
    let active = true;

    const watchAnchor = () => {
      if (!active) {
        return;
      }

      if (!element.isConnected) {
        setValue(defaultState);
        return;
      }

      frameId = requestAnimationFrame(watchAnchor);
    };

    frameId = requestAnimationFrame(watchAnchor);

    return () => {
      active = false;
      cancelAnimationFrame(frameId);
    };
  }, [value.element]);

  const anchorEl = isConnectedAnchor(value.element) ? value.element : null;

  const contextValue = React.useMemo(
    () => ({
      ...value,
      open,
      close,
    }),
    [close, open, value],
  );

  return (
    <PopoverContext.Provider value={contextValue}>
      {props.children}
      <ClickAwayListener onClickAway={close}>
        <Popper
          onMouseLeave={close}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          style={{ zIndex: 1300 }}
        >
          <div>{value.content}</div>
        </Popper>
      </ClickAwayListener>
    </PopoverContext.Provider>
  );
};

export function usePopover() {
  const context = React.useContext(PopoverContext);

  if (!context) {
    throw new Error('PopoverContext provider not found');
  }

  return context;
}

function Popover(props: Props) {
  const { children, content, actionsRef, disabled = false } = props;
  const context = usePopover();

  useImperativeHandle(actionsRef, () => ({
    close() {
      context.close();
    },
  }));

  React.useEffect(() => {
    if (disabled) {
      context.close();
    }
  }, [context.close, disabled]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    context.open({
      element: event.currentTarget,
      content,
    });
  };

  return <div onMouseEnter={handlePopoverOpen}>{children}</div>;
}

export default Popover;
